import { ComponentType, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Heading, Paragraph } from '@digdir/designsystemet-react';
import { ArrowRightIcon, CalendarIcon, ExclamationmarkTriangleIcon, PersonGroupIcon } from '@navikt/aksel-icons';
import type { CategoryInsight, HrTeamSummary } from '../../../../api/queries';
import { formatNames, formatScore, TeamFilter } from '../hrPsychologicalSafetyView';
import { agreementOf } from '../../../../domain/psychological-safety/team-result';
import classes from '../HRPsychologicalSafety.module.css';

type ActionCardsProps = {
  challengingTeams: HrTeamSummary[];
  weakestCategory?: CategoryInsight;
  missingTeams: HrTeamSummary[];
  /** Viser teamtabellen med valgt utvalg */
  onShowTeams: (filter: TeamFilter) => void;
};

type Action = {
  key: string;
  tone: 'danger' | 'warning' | 'accent';
  Icon: ComponentType<{ 'aria-hidden'?: boolean }>;
  kind: string;
  title: string;
  text: string;
  cta: string;
  href: string;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
};

/** Det HR bør se på først: utfordrende team, svakeste område og team uten nye målinger */
export const ActionCards = ({ challengingTeams, weakestCategory, missingTeams, onShowTeams }: ActionCardsProps) => {
  const { t, i18n } = useTranslation();
  const showTeams = (filter: TeamFilter) => (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    onShowTeams(filter);
  };

  const actions: Action[] = [];

  if (challengingTeams.length > 0) {
    actions.push({
      key: 'teams',
      tone: 'danger',
      Icon: ExclamationmarkTriangleIcon,
      kind: t('hrPsychologicalSafety.actions.teams.kind'),
      title: t('hrPsychologicalSafety.actions.teams.title', { count: challengingTeams.length }),
      text: t('hrPsychologicalSafety.actions.teams.text', {
        count: challengingTeams.length,
        names: formatNames(
          challengingTeams.map((team) => team.teamName),
          t('hrPsychologicalSafety.actions.teams.and'),
          (count) => t('hrPsychologicalSafety.actions.teams.others', { count }),
        ),
      }),
      cta: t('hrPsychologicalSafety.actions.showTeams'),
      href: '#hr-ps-teams',
      onClick: showTeams('followUp'),
    });
  }

  if (weakestCategory) {
    const agreement = agreementOf(weakestCategory.averageStandardDeviation);
    actions.push({
      key: 'area',
      tone: 'warning',
      Icon: PersonGroupIcon,
      kind: t('hrPsychologicalSafety.actions.area.kind'),
      title: t('hrPsychologicalSafety.actions.area.title', { name: weakestCategory.category }),
      text: [
        t('hrPsychologicalSafety.actions.area.text', {
          score: formatScore(weakestCategory.averageScore, i18n.language),
        }),
        agreement ? t(`hrPsychologicalSafety.actions.area.agreement.${agreement}`) : '',
      ]
        .filter(Boolean)
        .join(' '),
      cta: t('hrPsychologicalSafety.actions.area.cta'),
      href: '#hr-ps-areas',
    });
  }

  if (missingTeams.length > 0) {
    const unplanned = missingTeams.filter((team) => !team.hasScheduledSurvey).length;
    actions.push({
      key: 'missing',
      tone: 'accent',
      Icon: CalendarIcon,
      kind: t('hrPsychologicalSafety.actions.missing.kind'),
      title: t('hrPsychologicalSafety.actions.missing.title', { count: missingTeams.length }),
      text: [
        unplanned > 0 ? t('hrPsychologicalSafety.actions.missing.unplanned', { count: unplanned }) : '',
        t('hrPsychologicalSafety.actions.missing.text'),
      ]
        .filter(Boolean)
        .join(' '),
      cta: t('hrPsychologicalSafety.actions.showTeams'),
      href: '#hr-ps-teams',
      onClick: showTeams('missing'),
    });
  }

  return (
    <section aria-labelledby="hr-ps-actions" className={classes.section}>
      <div className={classes.sectionIntro}>
        <Heading level={2} data-size="md" id="hr-ps-actions">
          {t('hrPsychologicalSafety.actions.title')}
        </Heading>
        <Paragraph className={classes.subtle}>
          {actions.length > 0 ? t('hrPsychologicalSafety.actions.intro') : t('hrPsychologicalSafety.actions.none')}
        </Paragraph>
      </div>
      {actions.length > 0 && (
        <ul className={classes.actionGrid}>
          {actions.map(({ key, tone, Icon, kind, title, text, cta, href, onClick }) => (
            <li key={key} className={classes.actionCard}>
              <span className={classes.actionKind} data-tone={tone}>
                <span className={classes.actionIcon} aria-hidden="true">
                  <Icon aria-hidden />
                </span>
                {kind}
              </span>
              <Heading level={3} data-size="xs" className={classes.actionTitle}>
                {title}
              </Heading>
              <Paragraph data-size="sm">{text}</Paragraph>
              <a href={href} onClick={onClick} className={`ds-link ${classes.actionLink}`}>
                {cta}
                <ArrowRightIcon aria-hidden />
              </a>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
